/* Klaviyo client API: public Site ID only. Never add a private API key here. */
(function () {
  'use strict';
  if (customElements.get('rockem-signup-popup')) return;

  const DAY = 86400000;
  const REVISION = '2026-10-15';

  class RockemSignupPopup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      this.form = this.querySelector('[data-signup-form]');
      this.smsForm = this.querySelector('[data-signup-sms-form]');
      this.smsError = this.querySelector('[data-signup-sms-error]');
      this.interestsForm = this.querySelector('[data-signup-interests-form]');
      this.interestError = this.querySelector('[data-signup-interest-error]');
      this.interests = [];
      this.signupFlow = 'interests';
      this.step = 'interests';
      this.email = '';
      this.emailAccepted = false;
      this.submitButton = this.querySelector('[data-signup-submit]');
      this.submitLabel = this.querySelector('[data-signup-submit-label]') || this.submitButton;
      this.error = this.querySelector('[data-signup-error]');
      this.storageKey = 'rockem:signup:' + (this.dataset.campaign || 'newsletter-v1');
      this.localPreview = ['localhost', '127.0.0.1', '[::1]'].includes(location.hostname);
      this.testEnvironment = this.localPreview || location.hostname.endsWith('.shopifypreview.com');
      const params = new URLSearchParams(location.search);
      this.liveTest = this.testEnvironment && this.dataset.editor !== 'true' && params.get('rockem_signup_test') === '1';
      this.preview = !this.liveTest && (this.dataset.editor === 'true' || params.get('rockem_signup_preview') === '1');
      this.analyticsTest = this.testEnvironment || params.has('preview_theme_id') || Boolean(window.Shopify?.theme?.role && window.Shopify.theme.role !== 'main');
      this.analyticsDebug = this.analyticsTest && params.get('rockem_signup_analytics_debug') === '1' && this.dataset.editor !== 'true';
      this.previewVariant = (this.preview || this.liveTest || this.analyticsTest) && ['a', 'b'].includes(params.get('rockem_signup_variant')) ? params.get('rockem_signup_variant') : null;
      this.previewRequestedStep = params.has('rockem_signup_step');
      this.startedChannels = new Set();
      this.prepareAnalytics();
      const previewStep = params.get('rockem_signup_step');
      this.previewStep = previewStep === 'details' ? 'email' : ['email', 'sms', 'success'].includes(previewStep) ? previewStep : 'interests';
      if (this.previewStep === 'sms' && !this.smsForm) this.previewStep = 'email';
      this.configured = this.dataset.configured === 'true' && /^[a-z0-9]{6}$/i.test(this.dataset.siteId) && /^[a-z0-9]{6}$/i.test(this.dataset.listId) && (!this.smsForm || /^[a-z0-9]{6}$/i.test(this.dataset.smsListId));
      this.acceptedRequests = this.acceptedRequests || new Set();
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', () => this.fitViewport(), options);
        window.visualViewport.addEventListener('scroll', () => this.fitViewport(), options);
      }
      this.cmsReady = this.loadHero();

      this.querySelectorAll('[data-signup-close]').forEach(button => {
        button.addEventListener('click', () => this.close(), options);
      });
      this.dialog.addEventListener('cancel', event => {
        event.preventDefault();
        this.close();
      }, options);
      this.dialog.addEventListener('close', () => this.unlockPage(), options);
      this.form.addEventListener('submit', event => this.subscribe(event), options);
      this.interestsForm.addEventListener('submit', event => this.advance(event), options);
      this.interestsForm.addEventListener('change', event => {
        this.interestError.hidden = true;
        if (event.target.name === 'signup_interest' && event.target.checked) this.recordFavoriteChoice();
      }, options);
      if (this.smsForm) {
        this.prepareCountryPicker(options);
        this.smsForm.addEventListener('submit', event => this.subscribe(event), options);
        this.querySelector('[data-signup-sms-skip]').addEventListener('click', () => {
          if (this.step === 'sms' && !this.pending) {
            this.track('popup_sms_skip');
            this.finishSignup('email');
          }
        }, options);
      }
      [this.form, this.smsForm].filter(Boolean).forEach(form => {
        form.addEventListener('focusin', event => {
          const channel = form === this.smsForm ? 'sms' : 'email';
          const input = channel === 'sms' ? form.elements.phone : form.elements.email;
          if (event.target !== input || this.startedChannels.has(channel)) return;
          this.startedChannels.add(channel);
          this.track('popup_' + channel + '_start');
        }, options);
        form.addEventListener('input', () => {
          if (form === this.smsForm) this.sanitizePhoneInput(form.elements.phone);
          form.querySelectorAll('input').forEach(input => input.removeAttribute('aria-invalid'));
          (form === this.form ? this.error : this.smsError).hidden = true;
        }, options);
      });
      document.addEventListener('shopify:section:select', event => {
        if (this.dataset.editor === 'true' && event.detail.sectionId === this.dataset.sectionId) this.open();
      }, options);
      document.addEventListener('shopify:section:deselect', event => {
        if (event.detail.sectionId === this.dataset.sectionId) this.close(false);
      }, options);

      if (this.preview || this.liveTest) {
        if (this.dataset.editor !== 'true') {
          if (this.cmsPending) this.cmsReady.then(() => { if (this.isConnected) this.open(); });
          else this.open();
        }
      } else if (this.dataset.enabled === 'true' && this.configured && this.dataset.subscriber !== 'true' && !this.suppressed()) {
        this.timer = setTimeout(() => this.tryOpen(), Math.max(0, Number(this.dataset.delay) || 0) * 1000);
      }
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      if (this.request) this.request.abort();
      if (this.heroRequest) this.heroRequest.abort();
      if (this.dialog && this.dialog.open) this.dialog.close();
      this.unlockPage();
      if (this.listeners) this.listeners.abort();
    }

    prepareAnalytics() {
      // The existing google-tracking snippet loads gtag.js. Load only Shopify's
      // consent API here; configure the GA4 destination on the first allowed event.
      try {
        if (!window.Shopify?.customerPrivacy && typeof window.Shopify?.loadFeatures === 'function') {
          window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }], () => {});
        }
      } catch (_) { /* Tracking must never interrupt signup. */ }
    }

    track(event, parameters = {}) {
      try {
        const destination = this.dataset.ga4MeasurementId || '';
        if (!/^G-[A-Z0-9]+$/.test(destination) || this.preview || (this.analyticsTest && !this.analyticsDebug)) return;
        if (window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() !== true || typeof window.gtag !== 'function') return;
        const configured = window.rockemSignupGA4Destinations || (window.rockemSignupGA4Destinations = new Set());
        if (!configured.has(destination)) {
          // Popup events only: the Shopify integration owns page views/ecommerce.
          window.gtag('config', destination, { send_page_view: false });
          configured.add(destination);
        }
        window.gtag('event', event, {
          send_to: destination,
          popup_name: 'rockem_signup',
          popup_campaign: this.dataset.campaign || 'newsletter-v1',
          popup_step: this.step,
          popup_experiment: this.experiment?.id || 'none',
          popup_variant: this.experiment?.variant || 'default',
          ...(this.analyticsDebug ? { debug_mode: true } : {}),
          ...parameters
        });
      } catch (_) { /* Consent, blocked tags, or analytics failures cannot block signup. */ }
    }

    suppressed() {
      try {
        const record = JSON.parse(localStorage.getItem(this.storageKey));
        return record && Number(record.until) > Date.now();
      } catch (_) { return false; }
    }

    remember(days) {
      if (this.preview || this.liveTest) return;
      try {
        localStorage.setItem(this.storageKey, JSON.stringify({ until: Date.now() + days * DAY }));
      } catch (_) { /* Storage may be unavailable; signup still works. */ }
    }

    tryOpen() {
      if (this.suppressed()) return;
      if (this.cmsPending) {
        this.cmsReady.then(() => { if (this.isConnected) this.tryOpen(); });
        return;
      }
      // Wait while another modal is open or the tab is hidden.
      const modalOpen = Array.from(document.querySelectorAll('dialog[open], .modal.show, .modal.in, [aria-modal="true"]')).some(modal => {
        // The theme keeps account and size dialogs mounted while they are hidden.
        if (modal.closest('[hidden], [aria-hidden="true"]') || !modal.getClientRects().length) return false;
        const style = window.getComputedStyle(modal);
        return style.visibility !== 'hidden' && style.visibility !== 'collapse' && style.opacity !== '0';
      });
      if (document.hidden || modalOpen) {
        this.timer = setTimeout(() => this.tryOpen(), 1000);
        return;
      }
      this.open();
    }

    open() {
      clearTimeout(this.timer);
      if (this.dialog.open) return;
      this.previousFocus = document.activeElement;
      this.scrollPosition = window.scrollY;
      document.documentElement.style.setProperty('--signup-scroll-top', -this.scrollPosition + 'px');
      document.documentElement.classList.add('rockem-signup-open');
      this.locked = true;
      this.showStep(this.preview ? this.previewStep : this.step, false);
      this.dialog.showModal();
      this.startedChannels.clear();
      this.favoriteChosen = false;
      this.track('popup_view');
      if (this.step === 'email') this.track('popup_email_view');
      this.fitViewport();
      // Announce the current screen instead of leaving initial focus on Close.
      this.showStep(this.step);
      this.querySelectorAll('[data-signup-image]').forEach(img => { img.loading = 'eager'; });
    }

    fitViewport() {
      const viewport = window.visualViewport;
      if (!viewport || !this.dialog.open) return;
      const height = Math.min(viewport.height, window.innerHeight);
      this.dialog.style.setProperty('--signup-viewport-height', height + 'px');
      this.dialog.style.setProperty('--signup-viewport-top', viewport.offsetTop + 'px');
      this.dialog.dataset.compact = String(height < 440);
      this.dialog.dataset.tight = String(height < 330);
    }

    configureExperiment(config) {
      // Freeze one published treatment before opening; never change an open form.
      if (!config || config.status === 'off' || this.dialog.open || this.experimentConfigured) return;
      if (!/^[a-z0-9][a-z0-9_-]{2,63}$/.test(config.id || '') || !['running', 'paused', 'completed'].includes(config.status)) return;
      if (!Number.isInteger(config.allocation) || config.allocation < 10 || config.allocation > 90) return;
      for (const key of ['a', 'b']) {
        const candidate = config.variants?.[key];
        if (!candidate || !['interests', 'email_interests', 'email'].includes(candidate.firstStep)) return;
        if (Object.entries({ heading: 120, description: 240, buttonLabel: 60 }).some(([field, limit]) => candidate[field] != null && (typeof candidate[field] !== 'string' || candidate[field].length > limit))) return;
      }
      if (config.status === 'completed' && !['a', 'b'].includes(config.winner)) return;
      let variant = config.status === 'completed' ? config.winner : 'a';
      if (config.status === 'running') {
        const key = 'rockem:signup:experiment:' + config.id;
        let persist = false;
        try { persist = !this.preview && !this.liveTest && !this.analyticsTest && window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true; } catch (_) {}
        let saved;
        if (persist) {
          try { saved = JSON.parse(localStorage.getItem(key)); } catch (_) {}
        }
        variant = this.previewVariant || (saved && ['a', 'b'].includes(saved.variant) && saved.until > Date.now() ? saved.variant : Math.random() * 100 < config.allocation ? 'a' : 'b');
        if (persist) {
          try { localStorage.setItem(key, JSON.stringify({ variant, until: saved?.until > Date.now() ? saved.until : Date.now() + 90 * DAY })); } catch (_) {}
        }
        this.experiment = { id: config.id, variant };
      } else if (this.previewVariant) variant = this.previewVariant;
      this.experimentConfigured = true;
      const content = config.variants[variant];
      this.signupFlow = content.firstStep;
      this.step = content.firstStep === 'email_interests' ? 'email' : content.firstStep;
      if (this.preview && !this.previewRequestedStep) this.previewStep = this.step;
      if (content.heading) this.querySelectorAll('[data-signup-offer-heading]').forEach(node => { node.textContent = content.heading; });
      if (content.description) {
        const description = this.querySelector('[data-signup-email-description]');
        if (description) { description.textContent = content.description; description.style.whiteSpace = 'pre-line'; }
      }
      if (content.buttonLabel) this.querySelectorAll('[data-signup-submit-label], [data-signup-sms-submit-label]').forEach(node => { node.textContent = content.buttonLabel; });
    }

    async loadHero() {
      const endpoint = this.dataset.popupEndpoint || this.dataset.heroEndpoint;
      if (!endpoint) return;
      this.cmsPending = true;
      const controller = new AbortController();
      this.heroRequest = controller;
      const timeout = setTimeout(() => controller.abort(), 5000);
      const safeUrl = value => {
        try {
          const url = new URL(value);
          return url.protocol === 'https:' && !url.username && !url.password ? url.href : '';
        } catch (_) { return ''; }
      };
      const sourceSet = image => (Array.isArray(image.variants) ? image.variants : [])
        .filter(variant => safeUrl(variant.url) && Number(variant.width) > 0)
        .map(variant => safeUrl(variant.url) + ' ' + Math.round(Number(variant.width)) + 'w').join(', ');
      const position = (value, fallback) => Number.isFinite(Number(value)) && Number(value) >= 0 && Number(value) <= 100 ? Number(value) : fallback;
      const zoom = value => Number.isFinite(Number(value)) && Number(value) >= 1 && Number(value) <= 3 ? Number(value) : 1;
      try {
        const response = await fetch(endpoint, { credentials: 'omit', signal: controller.signal });
        if (!response.ok) return;
        const payload = await response.json();
        if (payload.published === true && this.isConnected) this.configureExperiment(payload.experiment);
        const hero = payload.published === true && payload.hero;
        if (!this.dataset.heroEndpoint || !hero || hero.enabled !== true || !safeUrl(hero.desktopImage?.src) || !this.isConnected) return;
        const image = this.querySelector('[data-signup-image]');
        const mobile = this.querySelector('[data-signup-mobile-source]');
        const mobileImage = hero.mobileImage && safeUrl(hero.mobileImage.src) ? hero.mobileImage : hero.desktopImage;
        // Set the picture source before its fallback so a CMS swap is consistent on phones.
        mobile.srcset = sourceSet(mobileImage) || safeUrl(mobileImage.src);
        image.srcset = sourceSet(hero.desktopImage);
        image.src = safeUrl(hero.desktopImage.src);
        image.classList.remove('rockem-signup__image--default');
        image.style.removeProperty('object-position');
        image.style.setProperty('--signup-desktop-focal', position(hero.desktopFocalX, 58) + '% ' + position(hero.desktopFocalY, 50) + '%');
        image.style.setProperty('--signup-mobile-focal', position(hero.mobileFocalX, 58) + '% ' + position(hero.mobileFocalY, 85) + '%');
        image.style.setProperty('--signup-desktop-scale', String(zoom(hero.desktopZoom)));
        image.style.setProperty('--signup-mobile-scale', String(zoom(hero.mobileZoom)));
      } catch (_) {
        // The Shopify image remains visible when CMS content is unavailable or unpublished.
      } finally {
        clearTimeout(timeout);
        this.heroRequest = null;
        this.cmsPending = false;
      }
    }

    close(remember = true) {
      if (!this.dialog.open) return;
      if (remember && !this.subscribed) this.track('popup_close', { email_accepted: this.emailAccepted });
      if (remember && !this.subscribed) this.remember(this.emailAccepted ? 365 : Number(this.dataset.dismissDays) || 7);
      this.dialog.close();
      this.unlockPage();
    }

    unlockPage() {
      if (!this.locked) return;
      this.locked = false;
      document.documentElement.classList.remove('rockem-signup-open');
      document.documentElement.style.removeProperty('--signup-scroll-top');
      window.scrollTo({ top: this.scrollPosition, behavior: 'instant' });
      if (this.previousFocus && this.previousFocus.isConnected) this.previousFocus.focus({ preventScroll: true });
    }

    sanitizePhoneInput(input) {
      // Preserve phone formatting and the caret when removing typed/pasted letters.
      const clean = value => value.replace(/[^0-9+().\s-]/g, '');
      const value = input.value;
      const filtered = clean(value);
      if (filtered === value) return;
      const start = input.selectionStart;
      const end = input.selectionEnd;
      input.value = filtered;
      if (start !== null && end !== null) {
        input.setSelectionRange(clean(value.slice(0, start)).length, clean(value.slice(0, end)).length);
      }
    }

    prepareCountryPicker(options) {
      const picker = this.querySelector('[data-signup-country]');
      const phone = window.libphonenumber;
      if (!picker || !phone) return;
      let names;
      try { names = new Intl.DisplayNames(['en'], { type: 'region' }); } catch (_) {}
      const countries = phone.getCountries().map(country => ({
        country,
        name: names ? names.of(country) : country,
        code: phone.getCountryCallingCode(country),
        flag: String.fromCodePoint(...Array.from(country, letter => 127397 + letter.charCodeAt(0)))
      })).sort((a, b) => a.name.localeCompare(b.name, 'en'));
      const fragment = document.createDocumentFragment();
      countries.forEach(({ country, name, code, flag }) => {
        const option = document.createElement('option');
        option.value = country;
        option.defaultSelected = country === 'US';
        option.textContent = flag + ' ' + name + ' (+' + code + ')';
        fragment.appendChild(option);
      });
      picker.replaceChildren(fragment);
      picker.value = 'US';
      const update = () => {
        const selected = countries.find(item => item.country === picker.value);
        if (!selected) return;
        this.querySelector('[data-signup-country-flag]').textContent = selected.flag;
        this.querySelector('[data-signup-country-code]').textContent = '+' + selected.code;
        this.smsError.hidden = true;
        this.smsForm.elements.phone.removeAttribute('aria-invalid');
      };
      picker.addEventListener('change', update, options);
      this.smsForm.addEventListener('reset', () => {
        // Form reset runs after the event, so explicitly reset the visible prefix too.
        picker.value = 'US';
        update();
      }, options);
      update();
    }

    normalizePhone(value) {
      const country = this.querySelector('[data-signup-country]')?.value || 'US';
      const phone = window.libphonenumber;
      if (phone) {
        const parsed = phone.parsePhoneNumberFromString(value, { defaultCountry: country, extract: false });
        return parsed && !parsed.ext && parsed.isPossible() ? parsed.number : '';
      }
      // Keep the existing US / explicit international fallback if the helper fails to load.
      let number = value.trim().replace(/[\s().-]/g, '');
      if (country === 'US' && /^\d{10}$/.test(number)) number = '+1' + number;
      else if (country === 'US' && /^1\d{10}$/.test(number)) number = '+' + number;
      return /^\+[1-9]\d{6,14}$/.test(number) ? number : '';
    }

    showError(message, channel = this.step, errorType = 'validation') {
      const error = channel === 'sms' ? this.smsError : this.error;
      error.textContent = message;
      error.hidden = false;
      this.track('popup_error', { popup_step: channel, error_type: errorType });
    }

    finishSignup(signupMethod = this.step === 'sms' ? 'email_sms' : 'email') {
      if (!this.subscribed) this.track('popup_complete', { signup_method: signupMethod });
      this.subscribed = true;
      this.remember(365);
      this.showStep('success');
      this.form.reset();
      if (this.smsForm) this.smsForm.reset();
      this.email = '';
      this.acceptedRequests.clear();
    }

    recordFavoriteChoice() {
      if (!this.dialog.open || this.step !== 'interests' || this.favoriteChosen) return;
      const count = this.querySelectorAll('[name="signup_interest"]:checked').length;
      if (!count) return;
      this.favoriteChosen = true;
      this.track('popup_favorite_chosen', { interest_count: count });
    }

    async advance(event) {
      event.preventDefault();
      if (this.step !== 'interests' || this.pending || this.subscribed) return;
      const selected = Array.from(this.querySelectorAll('[name="signup_interest"]:checked')).map(input => input.value);
      if (!selected.length) {
        this.interestError.textContent = 'Please choose a selection before proceeding.';
        this.interestError.hidden = false;
        this.track('popup_error', { error_type: 'validation' });
        return;
      }
      // A reopened dialog may retain checked choices without another change event.
      this.recordFavoriteChoice();
      this.interests = selected;
      this.interestError.hidden = true;
      if (this.signupFlow === 'email_interests') {
        if (!this.preview && !this.emailAccepted) {
          this.showStep('email');
          return;
        }
        if (!this.preview) {
          const button = this.interestsForm.querySelector('[type="submit"]');
          this.pending = true;
          button.disabled = true;
          this.interestsForm.setAttribute('aria-busy', 'true');
          try {
            // Save preferences without requesting email consent again or sending SMS.
            const { status, partialFailure } = await this.requestClient('profiles', {
              data: {
                type: 'profile',
                attributes: { email: this.email, properties: { rockem_signup_interests: selected } }
              }
            });
            if (!this.isConnected) return;
            if (status !== 202 || partialFailure) throw new Error('Preferences not accepted');
          } catch (_) {
            if (this.isConnected) {
              this.interestError.textContent = 'Your email signup was received, but we couldn’t save your favorites. Please try again.';
              this.interestError.hidden = false;
              this.track('popup_error', { error_type: 'preferences_failed' });
            }
            return;
          } finally {
            this.pending = false;
            button.disabled = false;
            this.interestsForm.removeAttribute('aria-busy');
          }
        }
        if (!this.isConnected) return;
        this.track('popup_interests_submit', { interest_count: selected.length });
        if (this.smsForm) this.showStep('sms');
        else this.finishSignup('email');
        return;
      }
      this.track('popup_interests_submit', { interest_count: selected.length });
      this.showStep('email');
    }

    showStep(step, focus = true) {
      const previousStep = this.step;
      this.step = step;
      const panels = {
        interests: this.querySelector('[data-signup-interests]'),
        email: this.querySelector('[data-signup-form-content]'),
        sms: this.querySelector('[data-signup-sms]'),
        success: this.querySelector('[data-signup-success]')
      };
      Object.entries(panels).forEach(([name, panel]) => { if (panel) panel.hidden = name !== step; });
      const prefixes = { interests: 'SignupInterests', email: 'Signup', sms: 'SignupSms', success: 'SignupSuccess' };
      this.dialog.setAttribute('aria-labelledby', prefixes[step] + 'Title-' + this.dataset.sectionId);
      this.dialog.setAttribute('aria-describedby', prefixes[step] + 'Description-' + this.dataset.sectionId);
      this.dialog.removeAttribute('aria-label');
      this.dialog.scrollTop = 0;
      if (focus && this.dialog.open) panels[step].querySelector('h2').focus({ preventScroll: true });
      if (this.dialog.open && previousStep !== step && ['email', 'sms'].includes(step)) this.track('popup_' + step + '_view');
    }

    requestSubscription(listId, attributes) {
      return this.requestClient('subscriptions', {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'RockEm fullscreen signup: ' + (this.dataset.campaign || 'newsletter-v1'),
            profile: { data: { type: 'profile', attributes } }
          },
          relationships: { list: { data: { type: 'list', id: listId } } }
        }
      });
    }

    async requestClient(resource, payload) {
      const controller = new AbortController();
      this.request = controller;
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch('https://a.klaviyo.com/client/' + resource + '?company_id=' + encodeURIComponent(this.dataset.siteId), {
          method: 'POST',
          headers: { 'Content-Type': 'application/vnd.api+json', Accept: 'application/vnd.api+json', revision: REVISION },
          body: JSON.stringify(payload),
          credentials: 'omit',
          signal: controller.signal
        });
        let detail = '';
        if (response.status !== 202 && this.liveTest && typeof response.json === 'function') {
          try {
            const body = await response.json();
            detail = (body.errors || []).map(error => error.detail || error.title || '').filter(Boolean).join(' ');
          } catch (_) { /* Keep the status-based error if no JSON body is returned. */ }
        }
        // HTTP 202 can still mean Klaviyo ignored an invalid identifier.
        // Browsers can read this header only when Klaviyo exposes it via CORS.
        const partialFailure = (response.headers.get('X-Klaviyo-Partial-Failure') || '').trim().toLowerCase();
        return { status: response.status, detail, partialFailure };
      } finally {
        clearTimeout(timeout);
        this.request = null;
      }
    }

    async subscribe(event) {
      event.preventDefault();
      if ((!this.preview && !this.configured) || !['email', 'sms'].includes(this.step) || this.pending || this.subscribed) return;
      const channel = this.step;
      const form = channel === 'sms' ? this.smsForm : this.form;
      if (!form) return;
      const input = channel === 'sms' ? form.elements.phone : form.elements.email;
      let email = channel === 'email' ? input.value.trim() : this.email;
      let phone = '';
      if (channel === 'email') {
        if (!email || !input.checkValidity()) {
          input.setAttribute('aria-invalid', 'true');
          this.showError('Please enter a valid email address.');
          return;
        }
      } else {
        phone = this.normalizePhone(input.value);
        if (!phone) {
          input.setAttribute('aria-invalid', 'true');
          this.showError('Please enter a valid phone number.');
          return;
        }
        if (!this.preview && (!email || !this.emailAccepted)) {
          this.showStep('email');
          this.showError('Please enter your email address first.');
          return;
        }
      }
      input.removeAttribute('aria-invalid');
      (channel === 'sms' ? this.smsError : this.error).hidden = true;

      // A separate SMS submission grants SMS consent. Skipping that screen never
      // submits a phone number or subscribes SMS, even when a number was entered.
      if (this.preview) {
        if (channel === 'email') this.email = email;
        if (channel === 'email' && this.signupFlow === 'email_interests') this.showStep('interests');
        else if (channel === 'email' && this.smsForm) this.showStep('sms');
        else this.finishSignup();
        return;
      }

      const job = {
        channel,
        listId: channel === 'sms' ? this.dataset.smsListId : this.dataset.listId,
        attributes: {
          email,
          ...(channel === 'sms' ? { phone_number: phone } : {}),
          ...(this.interests.length ? { properties: { rockem_signup_interests: this.interests } } : {}),
          subscriptions: { [channel]: { marketing: { consent: 'SUBSCRIBED' } } }
        }
      };
      const button = channel === 'sms' ? this.querySelector('[data-signup-sms-submit]') : this.submitButton;
      const label = channel === 'sms' ? this.querySelector('[data-signup-sms-submit-label]') : this.submitLabel;
      const skip = channel === 'sms' ? this.querySelector('[data-signup-sms-skip]') : null;
      this.pending = true;
      button.disabled = true;
      if (skip) skip.disabled = true;
      form.setAttribute('aria-busy', 'true');
      const originalLabel = label.textContent;
      label.textContent = 'JOINING…';
      this.track('popup_' + channel + '_submit');
      try {
        const requestKey = JSON.stringify(job);
        if (!this.acceptedRequests.has(requestKey)) {
          const { status, detail, partialFailure } = await this.requestSubscription(job.listId, job.attributes);
          if (!this.isConnected) return;
          if (status !== 202 || partialFailure) {
            const prefix = channel === 'sms' ? 'Your email signup was received, but your text signup wasn’t completed. ' : '';
            const partialMessage = partialFailure.includes('phone_number') ? 'Klaviyo did not accept the mobile number. Check the number and country code and try again.' : 'Klaviyo did not accept all your signup details. Check your details and try again.';
            this.showError(prefix + (partialFailure ? partialMessage : detail || (status === 429 ? 'Too many signup attempts. Please wait a minute and try again.' : 'Check your details and try again.')), channel, partialFailure ? 'partial_failure' : status === 429 ? 'rate_limit' : 'request_failed');
            return;
          }
          this.acceptedRequests.add(requestKey);
          this.track('popup_' + channel + '_accepted');
        }
        if (!this.isConnected) return;
        if (channel === 'email') {
          this.email = email;
          this.emailAccepted = true;
          if (this.signupFlow === 'email_interests' && this.dialog.open) this.showStep('interests');
          else if (this.smsForm && this.dialog.open) this.showStep('sms');
          else this.finishSignup();
        } else this.finishSignup();
      } catch (_) {
        if (this.isConnected) this.showError(channel === 'sms' ? 'Your email signup was received, but we couldn’t connect to complete your text signup. Please try again.' : 'Couldn’t connect right now. Please try again.', channel, 'network_error');
      } finally {
        this.pending = false;
        button.disabled = false;
        if (skip) skip.disabled = false;
        label.textContent = originalLabel;
        form.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('rockem-signup-popup', RockemSignupPopup);
}());
